import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Cpu, Wrench, Clock } from 'lucide-react';
import { Badge } from '../../../../ui/badge';
import { ToolCall, AgentProgressEvent } from '../../../../../hooks/useSimulationStream';

interface AgentActivityBlockProps {
  activatedSkills?: string[] | null;
  toolCalls: ToolCall[];
  agentProgress: AgentProgressEvent[];
  isComplete: boolean;
}

const AgentActivityBlock: React.FC<AgentActivityBlockProps> = ({
  activatedSkills,
  toolCalls,
  agentProgress,
  isComplete,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasSkills = activatedSkills && activatedSkills.length > 0;
  const hasToolCalls = toolCalls.length > 0;
  const hasProgress = agentProgress.length > 0;
  const hasData = hasSkills || hasToolCalls || hasProgress;

  if (!hasData || !isComplete) return null;

  const totalSteps = (activatedSkills?.length || 0) + toolCalls.length + agentProgress.length;

  return (
    <div className="border-b border-border bg-muted/30">
      {/* Collapsed header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 w-full px-4 py-2.5 text-left hover:bg-muted/50 transition-colors"
      >
        <Cpu className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        <span className="text-xs font-medium text-muted-foreground">Agent Activity</span>
        <span className="text-[10px] text-muted-foreground/60">{totalSteps} steps</span>
        <div className="flex-1" />
        {isExpanded ? (
          <ChevronDownIcon className="w-3.5 h-3.5 text-muted-foreground" />
        ) : (
          <ChevronRightIcon className="w-3.5 h-3.5 text-muted-foreground" />
        )}
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="px-4 pb-3 space-y-3 max-h-64 overflow-y-auto">
          {/* Skills loaded */}
          {hasSkills && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1.5 font-medium">
                Skills loaded
              </p>
              <div className="flex items-center gap-1.5 flex-wrap">
                {activatedSkills!.map((skill) => (
                  <Badge
                    key={skill}
                    variant="outline"
                    className="text-[10px] px-1.5 py-0 h-5 text-primary/70 border-primary/20 bg-primary/5"
                  >
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Tools used */}
          {hasToolCalls && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1.5 font-medium">
                Tools used
              </p>
              <div className="space-y-1">
                {toolCalls.map((tool, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11px] text-muted-foreground">
                    <Wrench className="w-3 h-3 mt-0.5 flex-shrink-0 text-muted-foreground/50" />
                    <span>{tool.message || tool.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Progress timeline */}
          {hasProgress && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70 mb-1.5 font-medium">
                Progress
              </p>
              <div className="relative pl-4 border-l border-border/60 space-y-1.5">
                {agentProgress.map((event, i) => (
                  <div key={i} className="relative">
                    <div className="absolute -left-[17px] top-1 w-2 h-2 rounded-full bg-muted-foreground/30" />
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <Clock className="w-3 h-3 flex-shrink-0 text-muted-foreground/50" />
                      <span className="font-medium text-muted-foreground/80">{event.phase}</span>
                      <span>{event.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgentActivityBlock;
